import { supabase } from '@/integrations/supabase/client';
import type { KnowledgeBaseDocument } from '@/types/supabase';
import { generateEmbedding } from '@/lib/embeddings';

interface SearchOptions {
  threshold?: number;
  includeContent?: boolean;
  limit?: number;
  textQuery?: string;
  logDiagnostics?: boolean;
  useQueryExpansion?: boolean;
}

function normalizeQuery(query: string) {
  return query
    .trim()
    .replace(/μg\/l/gi, 'ug/l')
    .replace(/micrograms per liter/gi, 'ug/l')
    .replace(/milligrams per liter/gi, 'mg/l');
}

function mapDocument(row: any): KnowledgeBaseDocument {
  return {
    id: row.id,
    knowledge_base_id: row.knowledge_base_id,
    name: row.name,
    content: row.content,
    file_type: row.file_type,
    size: row.size,
    created_at: row.created_at,
    updated_at: row.updated_at,
    storage_path: row.storage_path,
    embedding: Array.isArray(row.embedding) ? row.embedding : null,
    similarity: typeof row.similarity === 'number' ? row.similarity : undefined,
  };
}

async function rpcVectorSearch(knowledgeBaseId: string, queryEmbedding: number[], threshold: number, limit: number) {
  const { data, error } = await (supabase.rpc as any)('match_documents', {
    query_embedding: queryEmbedding,
    match_threshold: threshold,
    match_count: limit,
    knowledge_base_id: knowledgeBaseId,
  });

  if (error) throw error;
  return (data ?? []).map(mapDocument);
}

async function manualVectorSearch(knowledgeBaseId: string, queryEmbedding: number[], threshold: number, limit: number) {
  const { data, error } = await supabase
    .from('knowledge_base_documents')
    .select('*')
    .eq('knowledge_base_id', knowledgeBaseId)
    .not('embedding', 'is', null);

  if (error) throw error;

  const queryMagnitude = Math.sqrt(queryEmbedding.reduce((sum, value) => sum + value * value, 0));

  return (data ?? [])
    .filter((row: any) => Array.isArray(row.embedding) && row.embedding.length === queryEmbedding.length)
    .map((row: any) => {
      const documentEmbedding = row.embedding as number[];
      const dotProduct = documentEmbedding.reduce((sum, value, index) => sum + value * queryEmbedding[index], 0);
      const documentMagnitude = Math.sqrt(documentEmbedding.reduce((sum, value) => sum + value * value, 0));
      const similarity = dotProduct / (documentMagnitude * queryMagnitude || 1);
      return mapDocument({ ...row, similarity });
    })
    .filter((row) => (row.similarity ?? 0) >= threshold)
    .sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0))
    .slice(0, limit);
}

async function lexicalSearch(knowledgeBaseId: string, rawQuery: string, limit: number) {
  const query = `%${rawQuery}%`;
  const { data, error } = await supabase
    .from('knowledge_base_documents')
    .select('*')
    .eq('knowledge_base_id', knowledgeBaseId)
    .or(`name.ilike.${query},content.ilike.${query}`)
    .limit(limit);

  if (error) throw error;

  return (data ?? []).map((row: any) => mapDocument({ ...row, similarity: 0.15 }));
}

export async function semanticSearch(
  knowledgeBaseId: string,
  query: string,
  options: SearchOptions | number = {}
): Promise<KnowledgeBaseDocument[]> {
  const normalizedOptions = typeof options === 'number' ? { limit: options } : options;
  const {
    threshold = 0.2,
    limit = 10,
    textQuery = '',
    logDiagnostics = false,
  } = normalizedOptions;

  const normalizedQuery = normalizeQuery(textQuery || query);
  if (!normalizedQuery) return [];

  const lexical = await lexicalSearch(knowledgeBaseId, normalizedQuery, Math.min(limit, 6));

  try {
    const embedding = await generateEmbedding(normalizedQuery);
    if (!embedding) {
      return lexical.slice(0, limit);
    }

    const vectorMatches = await rpcVectorSearch(knowledgeBaseId, embedding, threshold, limit).catch(async (error) => {
      if (logDiagnostics) {
        console.warn('[semanticSearch] RPC search failed, falling back to manual search.', error);
      }
      return manualVectorSearch(knowledgeBaseId, embedding, threshold, limit);
    });

    const merged = new Map<string, KnowledgeBaseDocument>();
    [...vectorMatches, ...lexical].forEach((document) => {
      const existing = merged.get(document.id);
      if (!existing || (document.similarity ?? 0) > (existing.similarity ?? 0)) {
        merged.set(document.id, document);
      }
    });

    const results = Array.from(merged.values())
      .sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0))
      .slice(0, limit);

    if (logDiagnostics) {
      console.log('[semanticSearch] Results:', results.map((result) => ({ id: result.id, name: result.name, similarity: result.similarity })));
    }

    return results;
  } catch (error) {
    console.error('Error performing semantic search:', error);
    return lexical.slice(0, limit);
  }
}
